<script>
  import { createEventDispatcher } from 'svelte';
  import { selectedParticipant, selectedStudy } from '$stores/selectionStore';
  import RegularForm from '$lib/RegularForm.svelte';
  import MultiSectionForm from '$lib/MultiSectionForm.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faEye, faPlus, faTimes } from '@fortawesome/pro-light-svg-icons';

  const API_BASE_URL = '/api/proxy';
  const dispatch = createEventDispatcher();

  export let buttonLabel = 'Add Form';
  export let buttonTitle = 'Add Form';
  export let buttonClass =
    'bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-3 rounded-full text-xs flex items-center gap-1';
  export let iconOnly = false;
  export let disabled = false;

  let showAddFormModal = false;
  let showFormFillingModal = false;
  let formActivities = [];
  let isLoadingFormActivities = false;
  let publishedSchedule = null;
  let selectedFormDefinition = null;
  let selectedFormJSON = null;
  let formTranslationsForFilling = [];
  let selectedFillingLanguageGUID = null;
  let isLoadingFormDefinition = false;
  let formData = {};
  let formErrors = {};
  let addableFieldGroups = {};
  let currentSectionIndex = 0;
  let formSections = [];
  let isMultiSectionForm = false;
  let isFormTypeDetermined = false;
  let loadedScheduleStudyId = null;

  $: currentStudyId = $selectedStudy?._id || null;
  $: if (currentStudyId && currentStudyId !== loadedScheduleStudyId) {
    loadedScheduleStudyId = currentStudyId;
    fetchPublishedSchedule();
  }
  $: if (!currentStudyId) {
    loadedScheduleStudyId = null;
    publishedSchedule = null;
  }

  function normalizeGUID(id) {
    if (!id) return '';
    if (typeof id === 'string') return id;
    if (id.$oid) return id.$oid;
    if (id.toHexString) return id.toHexString();
    const str = id.toString ? id.toString() : '';
    const match = str.match(/[a-fA-F0-9]{24}/);
    return match ? match[0] : str;
  }

  function sanitizeJsonString(raw) {
    if (!raw || typeof raw !== 'string') return raw;
    return raw.replace(/\r?\n/g, '\\n');
  }

  function normalizeFormConfig(formConfig) {
    if (!formConfig || typeof formConfig !== 'object') return formConfig;
    if (formConfig.section && Array.isArray(formConfig.section.sections)) return formConfig;
    if (formConfig.type === 'sections' && Array.isArray(formConfig.sections)) {
      return { ...formConfig, section: formConfig };
    }
    return formConfig;
  }

  function parseFormConfig(rawFormConfig) {
    if (!rawFormConfig) return null;

    try {
      const parsed =
        typeof rawFormConfig === 'string'
          ? JSON.parse(sanitizeJsonString(rawFormConfig))
          : rawFormConfig;
      return normalizeFormConfig(parsed);
    } catch {
      return null;
    }
  }

  async function fetchPublishedSchedule() {
    if (!$selectedStudy?._id) {
      publishedSchedule = null;
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/schedules/schedulesByStudy?studyGUID=${$selectedStudy._id}`);
      if (!res.ok) {
        throw new Error('Failed to fetch study schedules');
      }

      const response = await res.json();
      const schedules = Array.isArray(response.data) ? response.data : [];
      publishedSchedule = schedules.find((schedule) => schedule.status === 'Published') || null;
    } catch (error) {
      console.error('Error fetching published schedule:', error);
      publishedSchedule = null;
    }
  }

  async function fetchFormNames(formGUIDs) {
    const nameMap = {};

    try {
      const requests = formGUIDs.map(async (guid) => {
        try {
          const res = await fetch(`${API_BASE_URL}/forms/${guid}`);
          if (!res.ok) return;

          const data = await res.json();
          nameMap[guid] = data.data?.name || 'Unknown Form';
        } catch (error) {
          console.warn(`Failed to fetch form name for GUID ${guid}:`, error);
        }
      });

      await Promise.all(requests);
    } catch (error) {
      console.error('Error fetching form names:', error);
    }

    return nameMap;
  }

  async function normalizeFormActivities(activities) {
    const normalized = [];
    const formGUIDsToFetch = new Set();

    for (const activity of activities) {
      if (activity.activityTypeCode === 'FORMGROUP' && Array.isArray(activity.formGUIDs)) {
        for (const guid of activity.formGUIDs) {
          formGUIDsToFetch.add(guid.toString ? guid.toString() : guid);
        }
      }
    }

    let formNameMap = {};
    if (formGUIDsToFetch.size > 0) {
      formNameMap = await fetchFormNames([...formGUIDsToFetch]);
    }

    for (const activity of activities) {
      if (activity.activityTypeCode === 'FORM') {
        normalized.push(activity);
        continue;
      }

      if (activity.activityTypeCode === 'FORMGROUP' && Array.isArray(activity.formGUIDs)) {
        for (const formGUID of activity.formGUIDs) {
          const guidStr = formGUID.toString ? formGUID.toString() : formGUID;
          normalized.push({
            ...activity,
            formGUID: guidStr,
            formName: formNameMap[guidStr] || 'Unknown Form',
            _isFromGroup: true,
            _groupName: activity.name,
            _groupDescription: activity.description,
          });
        }
      }
    }

    return normalized;
  }

  async function fetchFormActivities() {
    if (!publishedSchedule?._id) {
      formActivities = [];
      return;
    }

    try {
      isLoadingFormActivities = true;

      const res = await fetch(
        `${API_BASE_URL}/activities/bySchedule?scheduleGUID=${publishedSchedule._id}`
      );
      if (!res.ok) {
        throw new Error('Failed to fetch activities');
      }

      const response = await res.json();
      const activities = Array.isArray(response.data) ? response.data : [];
      const formTypeActivities = activities.filter(
        (activity) => activity.activityTypeCode === 'FORM' || activity.activityTypeCode === 'FORMGROUP'
      );

      formActivities = await normalizeFormActivities(formTypeActivities);
    } catch (error) {
      console.error('Error fetching form activities:', error);
      formActivities = [];
    } finally {
      isLoadingFormActivities = false;
    }
  }

  function getFormJSONForLanguage(translations, languageGUID) {
    if (!translations || !languageGUID) return null;
    const translation = translations.find((item) => item.languageGUID === languageGUID);
    return translation?.form || null;
  }

  function findEnUSLanguageGUID(translations) {
    const englishTranslation = translations.find((item) => item.languageCode === 'en_US');
    return englishTranslation?.languageGUID || null;
  }

  async function buildFormFillingTranslations(formDefinition) {
    const result = [];
    if (!formDefinition?.translations || !Array.isArray(formDefinition.translations)) {
      return result;
    }

    function extractLanguageName(display) {
      if (!display) return '';
      return display.replace(/^[\p{Emoji}\p{Emoji_Component}\s]+/u, '').trim();
    }

    let languagesMap = {};
    try {
      const studyId = $selectedStudy?._id;
      if (studyId) {
        const langRes = await fetch(`${API_BASE_URL}/languages/study/${studyId}`);
        if (langRes.ok) {
          const studyLanguages = await langRes.json();
          const languages = Array.isArray(studyLanguages) ? studyLanguages : [];

          languages.forEach((language) => {
            const languageGUID = normalizeGUID(language.languageGUID);
            const displayBase =
              language.displayBase || `${language.flag || ''} ${language.language || ''}`.trim();
            const code = language.code || '';

            languagesMap[languageGUID] = {
              code,
              languageName: extractLanguageName(displayBase),
              display: code ? `${displayBase} (${code})`.trim() : displayBase || 'Unknown',
            };
          });
        }
      }
    } catch (error) {
      console.warn('Could not fetch study languages for dropdown:', error);
    }

    for (const translation of formDefinition.translations) {
      if (!translation.form) continue;

      const languageGUID = normalizeGUID(translation.languageGUID);
      const languageInfo = languagesMap[languageGUID] || {};

      let displayName = languageInfo.display;
      if (!displayName && translation.languageDisplay) {
        displayName = translation.languageCode
          ? `${translation.languageDisplay} (${translation.languageCode})`
          : translation.languageDisplay;
      }
      if (!displayName && translation.languageCode) {
        displayName = translation.languageCode;
      }

      result.push({
        languageGUID,
        languageCode: languageInfo.code || translation.languageCode || null,
        languageName:
          languageInfo.languageName || extractLanguageName(translation.languageDisplay) || '',
        languageDisplay: displayName || 'Unknown',
        form: translation.form,
      });
    }

    result.sort((a, b) => (a.languageName || '').localeCompare(b.languageName || ''));
    return result;
  }

  async function getTranslatedFormJSON(formDefinition) {
    if (!formDefinition) return null;

    formTranslationsForFilling = await buildFormFillingTranslations(formDefinition);
    const participantLanguageGUID = normalizeGUID($selectedParticipant?.languageGUID);

    if (formTranslationsForFilling.length > 0) {
      const englishGUID = findEnUSLanguageGUID(formTranslationsForFilling);
      if (englishGUID) {
        selectedFillingLanguageGUID = englishGUID;
        const englishForm = getFormJSONForLanguage(formTranslationsForFilling, englishGUID);
        if (englishForm) {
          return englishForm;
        }
      }

      if (participantLanguageGUID) {
        const participantTranslation = formTranslationsForFilling.find(
          (translation) => translation.languageGUID === participantLanguageGUID
        );
        if (participantTranslation) {
          selectedFillingLanguageGUID = participantLanguageGUID;
          return participantTranslation.form;
        }
      }

      const firstTranslation = formTranslationsForFilling[0];
      if (firstTranslation) {
        selectedFillingLanguageGUID = firstTranslation.languageGUID;
        return firstTranslation.form;
      }
    }

    selectedFillingLanguageGUID = null;
    formTranslationsForFilling = [];
    return formDefinition.form;
  }

  async function handleFillingLanguageChange(event) {
    const newLanguageGUID = event.target.value;
    if (!newLanguageGUID || newLanguageGUID === selectedFillingLanguageGUID) {
      return;
    }

    selectedFillingLanguageGUID = newLanguageGUID;
    const newFormJSON = getFormJSONForLanguage(formTranslationsForFilling, newLanguageGUID);
    if (newFormJSON) {
      selectedFormJSON = newFormJSON;
    }
  }

  async function isQuestionnaireForm(form) {
    try {
      if (!form?.modelGUID) return false;

      const modelRes = await fetch(`${API_BASE_URL}/lookups/FORMMODEL`);
      if (!modelRes.ok) {
        return false;
      }

      const modelData = await modelRes.json();
      const formModels = modelData.data || [];
      const formModel = formModels.find((model) => model.id === form.modelGUID);
      return formModel?.code === 'QUESTIONNAIRE';
    } catch (error) {
      console.error('Error checking if form is questionnaire:', error);
      return false;
    }
  }

  function extractFormFields(section) {
    if (!section?.sections) return;

    section.sections.forEach((subSection) => {
      if (subSection.type === 'component' && subSection.component) {
        const component = subSection.component;
        const fieldName = component.name;

        switch (component.componentName) {
          case 'InputMultiCheckboxesComponent': {
            if (component.configuration?.defaultValue !== undefined) {
              const defaultValue = component.configuration.defaultValue;
              formData[fieldName] = Array.isArray(defaultValue) ? defaultValue : [];
            } else {
              formData[fieldName] = (component.configuration?.options || [])
                .filter((option) => option.default === true)
                .map((option) => option.code);
            }
            break;
          }
          case 'InputNumberComponent':
          case 'InputDateComponent':
          case 'InputDropdownComponent':
          case 'InputTextAreaComponent':
          case 'DTSQQuestionComponent':
          default:
            formData[fieldName] = component.configuration?.defaultValue ?? '';
        }
      } else if (subSection.type === 'sections' && subSection.sectionType === 'addableFields') {
        const sectionName = subSection.sectionName;
        addableFieldGroups[sectionName] = {
          section: subSection,
          groups: [0],
        };

        subSection.sections.forEach((componentSection) => {
          if (componentSection.type === 'component' && componentSection.component) {
            const fieldName = `${componentSection.component.name}[0]`;
            formData[fieldName] = '';
          }
        });
      } else if (subSection.sections) {
        extractFormFields(subSection);
      }
    });
  }

  function getFieldType(componentName) {
    switch (componentName) {
      case 'InputTextComponent':
        return 'text';
      case 'InputNumberComponent':
        return 'number';
      case 'InputDateComponent':
        return 'date';
      case 'InputTextAreaComponent':
        return 'textarea';
      case 'InputDropdownComponent':
        return 'select';
      case 'InputMultiCheckboxesComponent':
        return 'multiCheckbox';
      case 'DTSQQuestionComponent':
        return 'dtsq';
      default:
        return 'text';
    }
  }

  function renderSectionFields(section) {
    const fields = [];
    if (!section?.sections) return fields;

    section.sections.forEach((subSection) => {
      if (subSection.type === 'component' && subSection.component) {
        const component = subSection.component;
        fields.push({
          name: component.name,
          header: component.header,
          type: getFieldType(component.componentName),
          placeholder: component.placeholder,
          required: component.validations?.some((validation) => validation.required) || false,
          options: component.configuration?.options || [],
          rank: component.rank || null,
        });
      }
    });

    return fields;
  }

  function isFieldEmpty(value, fieldType) {
    if (value === null || value === undefined) return true;
    if (fieldType === 'multiCheckbox') {
      return !Array.isArray(value) || value.length === 0;
    }
    if (typeof value === 'string') return value.trim() === '';
    return false;
  }

  function validateFormFields(section) {
    if (!section?.sections) return;

    section.sections.forEach((subSection) => {
      if (subSection.type === 'component' && subSection.component) {
        const component = subSection.component;
        const fieldName = component.name;
        const value = formData[fieldName];
        const fieldType = getFieldType(component.componentName);
        const requiredValidation = component.validations?.find((validation) => validation.required);

        if (requiredValidation && isFieldEmpty(value, fieldType)) {
          formErrors[fieldName] = 'This field is required';
        }
      } else if (subSection.sections) {
        validateFormFields(subSection);
      }
    });
  }

  function validateForm() {
    formErrors = {};
    let isValid = true;
    if (!selectedFormDefinition) return false;

    try {
      const formConfig = parseFormConfig(selectedFormJSON || selectedFormDefinition.form);
      if (!formConfig) {
        return false;
      }

      if (isMultiSectionForm) {
        formSections.forEach((section) => {
          const fields = renderSectionFields(section);
          fields.forEach((field) => {
            if (field.required && isFieldEmpty(formData[field.name], field.type)) {
              formErrors[field.name] = 'This field is required';
              isValid = false;
            }
          });
        });
      } else {
        validateFormFields(formConfig.section);
        isValid = Object.keys(formErrors).length === 0;
      }
    } catch (error) {
      console.error('Error validating form:', error);
      isValid = false;
    }

    return isValid;
  }

  async function initializeFormData() {
    if (!selectedFormDefinition) return;

    try {
      isFormTypeDetermined = false;

      const formJSON = await getTranslatedFormJSON(selectedFormDefinition);
      if (!formJSON) {
        return;
      }

      selectedFormJSON = formJSON;
      const formConfig = parseFormConfig(formJSON);
      if (!formConfig) {
        return;
      }

      formData = {};
      formErrors = {};
      addableFieldGroups = {};
      currentSectionIndex = 0;
      formSections = [];
      isMultiSectionForm = false;

      if (await isQuestionnaireForm(selectedFormDefinition)) {
        isMultiSectionForm = true;

        if (formConfig.section?.sections) {
          const mainSection = formConfig.section.sections[0];
          if (mainSection?.sections) {
            const validSectionTypes = ['subscale', 'dtsqQuestions', 'questions', 'sections'];
            formSections = mainSection.sections.filter(
              (section) =>
                validSectionTypes.includes(section.sectionType) &&
                section.sections &&
                section.sections.length > 0
            );
          }
        }

        if (formSections.length === 0 && formConfig.sections) {
          const validSectionTypes = ['subscale', 'dtsqQuestions', 'questions', 'sections'];
          formSections = formConfig.sections.filter(
            (section) =>
              validSectionTypes.includes(section.sectionType) &&
              section.sections &&
              section.sections.length > 0
          );
        }

        if (formSections.length === 0 && formConfig.section?.sections) {
          const validSectionTypes = ['subscale', 'dtsqQuestions', 'questions', 'sections'];
          formSections = formConfig.section.sections.filter(
            (section) =>
              validSectionTypes.includes(section.sectionType) &&
              section.sections &&
              section.sections.length > 0
          );
        }
      }

      isFormTypeDetermined = true;
      extractFormFields(formConfig.section);
    } catch (error) {
      console.error('Error parsing form definition:', error);
    }
  }

  async function fetchFormDefinition(formGUID) {
    if (!formGUID) return;

    try {
      isLoadingFormDefinition = true;

      const res = await fetch(`${API_BASE_URL}/forms/${formGUID}`);
      if (!res.ok) {
        throw new Error('Failed to fetch form definition');
      }

      const response = await res.json();
      selectedFormDefinition = response.data;
      await initializeFormData();
    } catch (error) {
      console.error('Error fetching form definition:', error);
    } finally {
      isLoadingFormDefinition = false;
    }
  }

  async function openAddFormModal() {
    if (disabled || !$selectedParticipant || !$selectedStudy) {
      return;
    }

    if (!publishedSchedule) {
      await fetchPublishedSchedule();
    }

    showAddFormModal = true;
    await fetchFormActivities();
  }

  function closeAddFormModal() {
    showAddFormModal = false;
    formActivities = [];
  }

  async function handleAddForm(activity) {
    try {
      closeAddFormModal();
      await fetchFormDefinition(activity.formGUID);
      showFormFillingModal = true;
    } catch (error) {
      console.error('Error adding form:', error);
    }
  }

  function closeFormFillingModal() {
    showFormFillingModal = false;
    selectedFormDefinition = null;
    selectedFormJSON = null;
    formTranslationsForFilling = [];
    selectedFillingLanguageGUID = null;
    formData = {};
    formErrors = {};
    addableFieldGroups = {};
    currentSectionIndex = 0;
    formSections = [];
    isMultiSectionForm = false;
    isFormTypeDetermined = false;
  }

  function toUtcIsoFromTimezoneLocal(naiveLocalStr, timeZone) {
    if (!naiveLocalStr || typeof naiveLocalStr !== 'string') return null;

    const [datePart, timePartRaw] = naiveLocalStr.split('T');
    if (!datePart || !timePartRaw) return null;

    const [year, month, day] = datePart.split('-').map((n) => parseInt(n, 10));
    const [hour, minute, secondValue] = timePartRaw.split(':').map((n) => parseInt(n, 10));
    const second = Number.isNaN(secondValue) ? 0 : secondValue;

    const utcGuessMs = Date.UTC(year, (month || 1) - 1, day || 1, hour || 0, minute || 0, second);
    const guessDate = new Date(utcGuessMs);

    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset',
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    const parts = formatter.formatToParts(guessDate);
    const tzName = parts.find((part) => part.type === 'timeZoneName')?.value || '';
    const match = tzName.match(/([+-])(\d{1,2}):(\d{2})/);

    let offset = 'Z';
    if (match) {
      offset = `${match[1]}${match[2].padStart(2, '0')}:${match[3].padStart(2, '0')}`;
    }

    const isoWithOffset = `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(
      day
    ).padStart(2, '0')}T${String(hour || 0).padStart(2, '0')}:${String(minute || 0).padStart(
      2,
      '0'
    )}:${String(second).padStart(2, '0')}${offset}`;
    const date = new Date(isoWithOffset);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }

  function findFirstDateField(section) {
    if (!section?.sections) return null;

    for (const subSection of section.sections) {
      if (subSection.type === 'component' && subSection.component?.componentName === 'InputDateComponent') {
        return subSection.component.name;
      }

      if (subSection.sections) {
        const nested = findFirstDateField(subSection);
        if (nested) return nested;
      }
    }

    return null;
  }

  async function handleSubmitForm() {
    if (!validateForm()) {
      return;
    }

    try {
      let properties;

      if (selectedFormDefinition.name.includes('DTSQ')) {
        properties = {};
        Object.keys(formData).forEach((key) => {
          if (key.startsWith('dtsq_question_')) {
            properties[key] = formData[key];
          }
        });
      } else {
        properties = [];

        Object.keys(formData).forEach((key) => {
          if (!key.includes('[') && !key.startsWith('dtsq_question_')) {
            properties.push({
              component_name: key,
              value: formData[key],
            });
          }
        });

        Object.keys(addableFieldGroups).forEach((sectionName) => {
          const section = addableFieldGroups[sectionName];
          const values = [];

          section.groups.forEach((groupIndex) => {
            const groupData = [];
            section.section?.sections?.forEach((componentSection) => {
              if (componentSection.type === 'component' && componentSection.component) {
                const component = componentSection.component;
                const fieldName = `${component.name}[${groupIndex}]`;
                if (formData[fieldName] !== undefined) {
                  groupData.push({
                    component_name: component.name,
                    value: formData[fieldName],
                  });
                }
              }
            });

            if (groupData.length > 0) {
              values.push(groupData);
            }
          });

          if (values.length > 0) {
            properties.push({
              section_name: sectionName,
              values,
            });
          }
        });
      }

      let derivedFormDate = null;
      try {
        const formConfig = parseFormConfig(selectedFormJSON || selectedFormDefinition.form);
        const participantTz =
          $selectedParticipant?.timezoneCode || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
        const firstDateFieldName = findFirstDateField(formConfig?.section);

        if (firstDateFieldName && formData[firstDateFieldName]) {
          derivedFormDate = toUtcIsoFromTimezoneLocal(formData[firstDateFieldName], participantTz);
        }
      } catch {
        // fall back to current time below
      }

      const formJSONToSave = selectedFormJSON || selectedFormDefinition.form;
      const serializedFormJSON =
        typeof formJSONToSave === 'string' ? formJSONToSave : JSON.stringify(formJSONToSave);
      if (!serializedFormJSON?.trim()) {
        throw new Error('Form definition is missing or invalid');
      }

      const subjectFormData = {
        formGUID: selectedFormDefinition._id,
        name: selectedFormDefinition.name,
        form: serializedFormJSON,
        properties: JSON.stringify(properties),
        subjectGUID: $selectedParticipant._id,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        formDate: derivedFormDate || new Date().toISOString(),
      };

      const res = await fetch(`${API_BASE_URL}/forms/subjectForm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(subjectFormData),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to submit form');
      }

      const result = await res.json();
      closeFormFillingModal();
      dispatch('submitted', result);
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  }
</script>

<button type="button" on:click={openAddFormModal} class={buttonClass} title={buttonTitle} disabled={disabled}>
  <FontAwesomeIcon icon={faPlus} />
  {#if !iconOnly}
    <span>{buttonLabel}</span>
  {/if}
</button>

{#if showAddFormModal}
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div class="mx-4 max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 dark:bg-gray-800">
      <div class="mb-4 flex items-center justify-between">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Add Form Activity</h2>
        <button
          type="button"
          on:click={closeAddFormModal}
          class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <FontAwesomeIcon icon={faTimes} class="h-5 w-5" />
        </button>
      </div>

      {#if isLoadingFormActivities}
        <div class="flex h-32 flex-col items-center justify-center">
          <div class="loader mb-4"></div>
          <p class="text-sm text-gray-700 dark:text-gray-300">Loading form activities...</p>
        </div>
      {:else if formActivities.length === 0}
        <div class="py-8 text-center">
          <p class="text-gray-500 dark:text-gray-400">
            No form activities found in the published schedule.
          </p>
        </div>
      {:else}
        <div class="space-y-2">
          <p class="mb-4 text-sm text-gray-600 dark:text-gray-400">
            Select a form activity from the published schedule to add to this participant's forms:
          </p>

          {#each formActivities as activity}
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <!-- svelte-ignore a11y-no-static-element-interactions -->
            <div
              class="cursor-pointer rounded-lg border border-gray-200 p-4 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
              on:click={() => handleAddForm(activity)}
            >
              <div class="flex items-center justify-between">
                <div class="flex-1">
                  <h3 class="font-medium text-gray-900 dark:text-white">
                    {activity._isFromGroup ? activity.formName : activity.name}
                  </h3>
                  {#if activity.description}
                    <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {activity.description}
                    </p>
                  {/if}
                  <div class="mt-2 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                    {#if activity.formName}
                      <span>Form: {activity.formName}</span>
                    {/if}
                    {#if activity.code}
                      <span>Code: {activity.code}</span>
                    {/if}
                    {#if activity.onDemand}
                      <span class="text-blue-600 dark:text-blue-400">On Demand</span>
                    {/if}
                  </div>
                </div>
                <FontAwesomeIcon icon={faEye} class="ml-4 text-sky-800 dark:text-white/70" />
              </div>
            </div>
          {/each}
        </div>
      {/if}

      <div class="mt-6 flex justify-end">
        <button
          type="button"
          on:click={closeAddFormModal}
          class="rounded bg-gray-500 px-4 py-2 text-white transition-colors hover:bg-gray-700"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
{/if}

{#if showFormFillingModal}
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div class="mx-4 max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white p-6 dark:bg-gray-800">
      <div class="mb-4 flex items-center justify-between">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
          {selectedFormDefinition?.name || 'Form'}
        </h2>
        <div class="flex items-center gap-2">
          {#if formTranslationsForFilling.length > 1}
            {@const participantLangGUID = normalizeGUID($selectedParticipant?.languageGUID)}
            <select
              value={selectedFillingLanguageGUID}
              on:change={handleFillingLanguageChange}
              class="min-w-[13.5rem] rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
            >
              {#each formTranslationsForFilling as translation}
                {@const isParticipantLang = participantLangGUID && translation.languageGUID === participantLangGUID}
                <option value={translation.languageGUID}>
                  {translation.languageDisplay || translation.languageCode || 'Unknown'}{isParticipantLang
                    ? ' (Participant)'
                    : ''}
                </option>
              {/each}
            </select>
          {/if}
          <button
            type="button"
            on:click={closeFormFillingModal}
            class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <FontAwesomeIcon icon={faTimes} class="h-5 w-5" />
          </button>
        </div>
      </div>

      {#if isLoadingFormDefinition}
        <div class="flex h-32 flex-col items-center justify-center">
          <div class="loader mb-4"></div>
          <p class="text-sm text-gray-700 dark:text-gray-300">Loading form definition...</p>
        </div>
      {:else if selectedFormDefinition}
        <div class="space-y-4">
          {#if selectedFormJSON}
            {@const formConfig = (() => parseFormConfig(selectedFormJSON))()}
            {#if formConfig}
              {#if !isFormTypeDetermined}
                <div class="flex items-center justify-center py-8">
                  <div class="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
                  <span class="ml-3 text-gray-600 dark:text-gray-400">Loading form...</span>
                </div>
              {:else if isMultiSectionForm && formSections.length > 0}
                {@const questionnaireInfo = (() => {
                  try {
                    const parsedConfig = parseFormConfig(selectedFormJSON) || {};
                    return {
                      name: parsedConfig.name || null,
                      description: parsedConfig.description || null,
                      copyright: parsedConfig.copyright || null,
                    };
                  } catch {
                    return null;
                  }
                })()}
                <MultiSectionForm
                  {formSections}
                  {currentSectionIndex}
                  {formData}
                  {formErrors}
                  isEditing={false}
                  {questionnaireInfo}
                  on:sectionChange={(event) => (currentSectionIndex = event.detail.index)}
                  on:cancel={closeFormFillingModal}
                  on:submit={handleSubmitForm}
                />
              {:else}
                <RegularForm
                  {formConfig}
                  {formData}
                  {formErrors}
                  {addableFieldGroups}
                  isEditing={false}
                  on:cancel={closeFormFillingModal}
                  on:submit={handleSubmitForm}
                />
              {/if}
            {:else}
              <p class="text-gray-500 dark:text-gray-400">Error loading form configuration.</p>
            {/if}
          {:else}
            <p class="text-gray-500 dark:text-gray-400">No form definition available.</p>
          {/if}
        </div>
      {/if}
    </div>
  </div>
{/if}
